import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Divider
} from "@mui/material";
import { Stepper, Step, StepLabel } from "@material-ui/core";
import { format, parseISO } from "date-fns";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ShowTicketLogModal = ({ isOpen, handleClose, ticketId }) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const typeDescriptions = {
    create: i18n.t("showTicketLogModal.options.create"),
    chatBot: i18n.t("showTicketLogModal.options.chatBot"),
    queue: i18n.t("showTicketLogModal.options.queue"),
    open: i18n.t("showTicketLogModal.options.open"),
    access: i18n.t("showTicketLogModal.options.access"),
    transfered: i18n.t("showTicketLogModal.options.transfered"),
    receivedTransfer: i18n.t("showTicketLogModal.options.receivedTransfer"),
    pending: i18n.t("showTicketLogModal.options.pending"),
    closed: i18n.t("showTicketLogModal.options.closed"),
    reopen: i18n.t("showTicketLogModal.options.reopen"),
    redirect: i18n.t("showTicketLogModal.options.redirect"),
    clientClosed: i18n.t("showTicketLogModal.options.clientClosed")
  };

  const formatDate = (value) => {
    if (!value) return "";
    return format(parseISO(value), "dd/MM/yyyy HH:mm");
  };

  const formatLogLabel = (log) => {
    const dateLabel = formatDate(log?.createdAt);

    if (log?.type === "receivedTransfer") {
      return `Transferido para o usuário ${log?.user?.name || "Sistema"} - ${dateLabel}`;
    }

    if (log?.type === "queue" || log?.type === "redirect") {
      return `${log?.queue?.name || "—"} ${typeDescriptions[log.type]} - ${dateLabel}`;
    }

    if (
      log?.type === "access" ||
      log?.type === "transfered" ||
      log?.type === "open" ||
      log?.type === "pending" ||
      log?.type === "closed" ||
      log?.type === "reopen"
    ) {
      return `${log?.user?.name || "Sistema"} ${typeDescriptions[log.type]} - ${dateLabel}`;
    }

    return `${typeDescriptions[log?.type] || log?.type || ""} - ${dateLabel}`;
  };

  const chronologicalLogs = useMemo(() => {
    return [...logs].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [logs]);

  const handoffIndex = useMemo(
    () => chronologicalLogs.findIndex((log) => log?.type === "receivedTransfer"),
    [chronologicalLogs]
  );

  const sections = useMemo(() => {
    if (!chronologicalLogs.length) return [];

    if (handoffIndex === -1) {
      return [
        {
          key: "lyra-only",
          title: "Histórico da LYRA",
          subtitle: "Não houve transferência para humano neste ticket.",
          chip: "LYRA",
          logs: chronologicalLogs
        }
      ];
    }

    const transferLog = chronologicalLogs[handoffIndex];
    const beforeTransfer = chronologicalLogs.slice(0, handoffIndex);
    const afterTransfer = chronologicalLogs.slice(handoffIndex + 1);
    const transferName = transferLog?.user?.name || "usuário responsável";

    return [
      {
        key: "lyra",
        title: "Histórico da LYRA",
        subtitle: "Tudo o que aconteceu antes do handoff para humano.",
        chip: "LYRA",
        logs: beforeTransfer
      },
      {
        key: "handoff",
        title: `Transferido para o usuário ${transferName}`,
        subtitle: "O ticket saiu da condução da LYRA e passou para o humano.",
        chip: "TRANSFERÊNCIA",
        logs: transferLog ? [transferLog] : []
      },
      {
        key: "human",
        title: "Histórico do humano",
        subtitle: "Interações registradas depois que o ticket foi assumido.",
        chip: "HUMANO",
        logs: afterTransfer
      }
    ].filter((section) => section.logs.length > 0);
  }, [chronologicalLogs, handoffIndex]);

  useEffect(() => {
    if (!isOpen || !ticketId) return;

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchLogs = async () => {
        try {
          const { data } = await api.get(`/tickets-log/${ticketId}`);
          setLogs(Array.isArray(data) ? data : []);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchLogs();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [isOpen, ticketId]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Histórico do ticket</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Aqui a leitura fica separada entre a etapa da LYRA e a etapa do humano que assumiu o atendimento.
          </Typography>
        </Box>

        {loading && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Carregando histórico...
            </Typography>
          </Paper>
        )}

        {!loading && sections.length === 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum histórico encontrado para este ticket.
            </Typography>
          </Paper>
        )}

        {!loading &&
          sections.map((section) => (
            <Paper
              key={section.key}
              variant="outlined"
              sx={{ p: 2, mb: 2, borderLeft: "4px solid #0b5d9a" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.subtitle}
                  </Typography>
                </Box>
                <Chip
                  label={section.chip}
                  size="small"
                  color={section.chip === "HUMANO" ? "primary" : "default"}
                  variant={section.chip === "TRANSFERÊNCIA" ? "filled" : "outlined"}
                />
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Stepper activeStep={-1} orientation="vertical">
                {section.logs.map((log, index) => (
                  <Step key={`${section.key}-${index}`}>
                    <StepLabel>{formatLogLabel(log)}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          ))}

        {!loading && handoffIndex !== -1 && (
          <Box sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fbff" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Leitura operacional
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tudo acima da transferência pertence à condução da LYRA. Tudo abaixo passa a registrar a atuação do humano que assumiu o ticket.
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowTicketLogModal;
